import React from 'react';

import { MoveMapView, MoveLegendTable } from '@features/mapVisualization';
import { classifyMove } from '@features/mapVisualization/constants';
import type { UseDriverMapDataReturn } from '@features/moves/map/useDriverMapData';
import { useResizeHandle } from '@features/moves/map/useResizeHandle';
import DriverSelectionSidebar from '@features/moves/map/DriverSelectionSidebar';
import DriverStatusMarker from '@features/moves/map/DriverStatusMarker';
import SelectedDriverOverlay from '@features/moves/map/SelectedDriverOverlay';
import { DEFAULT_US_BOUNDS } from '@utils/googleMapOptions';
import { decodePolyline } from '@utils/decodePolyline';

import { useRooftop, useCustomerConfig } from '@store';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography } from '@mui/material';
import { BiInfoCircle } from 'react-icons/bi';

// HELPERS ------------------------------------------------------------------- //

const radiusMilesToZoom = (radiusMiles: number): number => {
  if (radiusMiles <= 0) return 12;
  return Math.max(8, Math.min(15, Math.round(14 - Math.log2(radiusMiles))));
};

// MAP ID -------------------------------------------------------------------- //

const MAP_ID = 'driver-map';

// COMPONENT ----------------------------------------------------------------- //

interface DriverMapProps {
  /** The host tab owns the hook that produces this. */
  data: UseDriverMapDataReturn;
}

const DriverMap = ({ data }: DriverMapProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();

  const {
    moves,
    drivers,
    moveColorMap,
    pingsByMoveId,
    unassignedPings,
    selectedDriverId,
    handleSelectDriver,
    highlightedMoveId,
    setHighlightedMoveId,
    driverMoves,
    loading,
    pingsLoading,
    movesTruncated,
    driversTruncated,
    totalMoveCount,
    refetch,
  } = data;

  // Customer center for default map view
  const customerCenter = React.useMemo(() => {
    const lat = rooftop?.location?.latitude;
    const lng = rooftop?.location?.longitude;
    if (lat != null && lng != null) return { lat: Number(lat), lng: Number(lng) };
    return undefined;
  }, [rooftop?.location?.latitude, rooftop?.location?.longitude]);

  const customerZoom = React.useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const radiusMiles = (customerConfig as any)?.customer_serviceable_radius_miles || 0;
    return radiusMilesToZoom(radiusMiles);
  }, [customerConfig]);

  // Compute default bounds from driver locations
  const defaultBounds = React.useMemo(() => {
    const withLoc = drivers.filter(d => d.gpslocations?.[0]?.location?.coordinates?.length);
    const onlineWithLoc = withLoc.filter(d => d.status === 'online');

    if (onlineWithLoc.length > 0) {
      const lats = onlineWithLoc.map(d => d.gpslocations![0].location.coordinates[1]);
      const lngs = onlineWithLoc.map(d => d.gpslocations![0].location.coordinates[0]);
      const padding = 0.05;
      return {
        north: Math.max(...lats) + padding,
        south: Math.min(...lats) - padding,
        west: Math.min(...lngs) - padding,
        east: Math.max(...lngs) + padding,
      };
    }
    if (customerCenter) {
      const zoom = customerZoom || 12;
      const padding = 0.3 / Math.pow(2, zoom - 10);
      return {
        north: customerCenter.lat + padding,
        south: customerCenter.lat - padding,
        west: customerCenter.lng - padding,
        east: customerCenter.lng + padding,
      };
    }
    return DEFAULT_US_BOUNDS;
  }, [drivers, customerCenter, customerZoom]);

  // Refs for map instance and fit-bounds fingerprints
  const mapRef = React.useRef<google.maps.Map | null>(null);
  const preSelectionViewRef = React.useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(null);
  const lastFitFingerprintRef = React.useRef<string>('');
  const lastDriverFitRef = React.useRef<string>('');

  const handleMapReady = React.useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Auto-fit bounds when data changes and no driver is selected
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || loading || selectedDriverId) return;

    const moveIds = moves.map(m => m.id).sort().join(',');
    const driverIds = drivers.map(d => d.id).sort().join(',');
    const fingerprint = `${moveIds}|${driverIds}`;
    if (fingerprint === lastFitFingerprintRef.current) return;
    lastFitFingerprintRef.current = fingerprint;

    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    moves.forEach(move => {
      if (move.lane?.pickup?.latitude != null && move.lane?.pickup?.longitude != null) {
        bounds.extend({ lat: move.lane.pickup.latitude, lng: move.lane.pickup.longitude });
        hasPoints = true;
      }
      if (move.lane?.delivery?.latitude != null && move.lane?.delivery?.longitude != null) {
        bounds.extend({ lat: move.lane.delivery.latitude, lng: move.lane.delivery.longitude });
        hasPoints = true;
      }
    });

    drivers.forEach(driver => {
      const coords = driver.gpslocations?.[0]?.location?.coordinates;
      if (coords?.length) {
        bounds.extend({ lat: coords[1], lng: coords[0] });
        hasPoints = true;
      }
    });

    if (hasPoints) {
      map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
    }
  }, [moves, drivers, loading, selectedDriverId]);

  // Save view on driver selection; clear fingerprints on deselection so auto-fit re-runs
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (selectedDriverId) {
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (center && zoom != null && !preSelectionViewRef.current) {
        preSelectionViewRef.current = {
          center: { lat: center.lat(), lng: center.lng() },
          zoom,
        };
      }
    } else {
      preSelectionViewRef.current = null;
      lastFitFingerprintRef.current = '';
      lastDriverFitRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverId]);

  // Fit bounds to driver data — zooms to highlighted move or all driver moves + position
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedDriverId || loading) return;

    const fingerprint = `${selectedDriverId}|${highlightedMoveId ?? ''}|${driverMoves.map(m => m.id).join(',')}`;
    if (fingerprint === lastDriverFitRef.current) return;
    lastDriverFitRef.current = fingerprint;

    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    if (highlightedMoveId) {
      // Fit to single highlighted move
      const move = driverMoves.find(m => m.id === highlightedMoveId);
      if (move) {
        if (move.lane?.pickup?.latitude != null && move.lane?.pickup?.longitude != null) {
          bounds.extend({ lat: move.lane.pickup.latitude, lng: move.lane.pickup.longitude });
          hasPoints = true;
        }
        if (move.lane?.delivery?.latitude != null && move.lane?.delivery?.longitude != null) {
          bounds.extend({ lat: move.lane.delivery.latitude, lng: move.lane.delivery.longitude });
          hasPoints = true;
        }
      }
    } else {
      // Fit to all driver moves + driver position
      driverMoves.forEach(move => {
        if (move.lane?.pickup?.latitude != null && move.lane?.pickup?.longitude != null) {
          bounds.extend({ lat: move.lane.pickup.latitude, lng: move.lane.pickup.longitude });
          hasPoints = true;
        }
        if (move.lane?.delivery?.latitude != null && move.lane?.delivery?.longitude != null) {
          bounds.extend({ lat: move.lane.delivery.latitude, lng: move.lane.delivery.longitude });
          hasPoints = true;
        }
      });

      const driver = drivers.find(d => d.id === selectedDriverId);
      const coords = driver?.gpslocations?.[0]?.location?.coordinates;
      if (coords?.length) {
        bounds.extend({ lat: coords[1], lng: coords[0] });
        hasPoints = true;
      }
    }

    if (hasPoints) {
      map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
    }
  }, [selectedDriverId, highlightedMoveId, driverMoves, loading, drivers]);

  // Selected driver object
  const selectedDriver = React.useMemo(
    () => selectedDriverId != null ? drivers.find(d => d.id === selectedDriverId) ?? null : null,
    [drivers, selectedDriverId]
  );

  // Drivers with valid locations
  const driversWithLocations = React.useMemo(
    () => drivers.filter(d => d.gpslocations?.[0]?.location?.coordinates?.length),
    [drivers]
  );

  // Build driverId → decoded route path for each driver's active move
  // Used for road-constrained dead reckoning
  const driverRoutePaths = React.useMemo(() => {
    const map = new Map<number, { lat: number; lng: number }[]>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    moves.forEach((move: any) => {
      const driverId = move.driver_id;
      if (!driverId || map.has(driverId)) return;
      if (classifyMove(move) !== 'active') return;
      const encoded = move.lane?.route_polyline;
      if (!encoded) return;
      map.set(driverId, decodePolyline(encoded));
    });
    return map;
  }, [moves]);

  // Geofence visibility toggle
  const [showGeofences, setShowGeofences] = React.useState(true);
  const handleToggleGeofences = React.useCallback(() => setShowGeofences(prev => !prev), []);

  const truncationWarning = movesTruncated || driversTruncated;

  // Resizable sidebar (drag left edge to widen/narrow)
  const sidebar = useResizeHandle({ initial: 300, min: 200, max: 500, direction: 'horizontal', inverted: true, storageKey: 'moves-map-sidebar-width' });
  // Resizable legend panel (drag top edge to grow/shrink)
  const legend = useResizeHandle({ initial: 240, min: 100, max: 500, direction: 'vertical', inverted: true, storageKey: 'moves-map-legend-height' });

  return (
    <div className={cls.wrapper}>
      {/* Map + overlays */}
      <div className={cls.mapContent}>
        <div className={cls.mapZone}>
          <MoveMapView
            mapId={MAP_ID}
            moves={driverMoves}
            pingsByMoveId={pingsByMoveId}
            unassignedPings={unassignedPings}
            highlightedMoveId={highlightedMoveId}
            moveColorMap={moveColorMap}
            selectedDriverId={selectedDriverId}
            rooftopId={rooftop?.id}
            loading={pingsLoading && !!selectedDriverId}
            showGeofences={showGeofences}
            defaultBounds={defaultBounds}
            onMapReady={handleMapReady}
            topLeftOverlay={
              selectedDriverId ? (
                <SelectedDriverOverlay
                  selectedDriver={selectedDriver}
                  driverMoves={driverMoves}
                  pingsByMoveId={pingsByMoveId}
                  unassignedPings={unassignedPings}
                  highlightedMoveId={highlightedMoveId}
                  moveColorMap={moveColorMap}
                />
              ) : undefined
            }
            bottomOverlay={
              truncationWarning ? (
                <div className={cls.warningBanner}>
                  <BiInfoCircle size={16} />
                  <Typography variant='body2' sx={{ fontSize: 12 }}>
                    Showing {moves.length} of {totalMoveCount} moves. Narrow your filters to see all.
                  </Typography>
                </div>
              ) : undefined
            }
          >
            {driversWithLocations.map(driver => (
              <DriverStatusMarker
                key={driver.id}
                mapId={MAP_ID}
                driver={driver}
                isSelected={selectedDriverId === driver.id}
                routePath={driverRoutePaths.get(driver.id)}
                onClick={() => {
                  if (selectedDriverId === driver.id) {
                    handleSelectDriver(null);
                  } else {
                    handleSelectDriver(driver.id);
                  }
                }}
              />
            ))}
          </MoveMapView>
        </div>

        {/* Legend table below map */}
        {selectedDriverId && driverMoves.length > 0 ? (
          <>
            <div className={cls.resizeHandleH} {...legend.handleProps} />
            <div className={cls.legendArea} style={{ flexBasis: legend.size, maxHeight: legend.size }}>
              <MoveLegendTable
                moves={driverMoves}
                moveColorMap={moveColorMap}
                highlightedMoveId={highlightedMoveId}
                onHighlightMove={setHighlightedMoveId}
                loading={loading}
                showGeofences={showGeofences}
                onToggleGeofences={handleToggleGeofences}
              />
            </div>
          </>
        ) : null}
      </div>

      {/* Sidebar resize handle + driver sidebar */}
      <div className={cls.resizeHandleV} {...sidebar.handleProps} />
      <div className={cls.sidebarArea} style={{ flexBasis: sidebar.size, minWidth: sidebar.size, maxWidth: sidebar.size }}>
        <DriverSelectionSidebar
          drivers={drivers}
          selectedDriverId={selectedDriverId}
          onSelectDriver={handleSelectDriver}
          onRefresh={() => refetch()}
        />
      </div>
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  wrapper: css`
    display: flex;
    height: 100%;
    width: 100%;
    overflow: hidden;
  `,
  mapContent: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  `,
  mapZone: css`
    flex: 1;
    min-height: 0;
    position: relative;
  `,
  legendArea: css`
    flex: 0 0 auto;
    min-height: 0;
    overflow: auto;
    border-top: 1px solid ${theme?.palette?.divider};
    background-color: ${theme?.palette?.background?.paper};
  `,
  sidebarArea: css`
    flex: 0 0 auto;
    border-left: 1px solid ${theme?.palette?.divider};
    height: 100%;
    overflow: hidden;
  `,
  resizeHandleV: css`
    flex: 0 0 4px;
    cursor: col-resize;
    background-color: transparent;
    transition: background-color 0.15s ease;
    &:hover {
      background-color: ${theme?.palette?.primary?.main}40;
    }
    &:active {
      background-color: ${theme?.palette?.primary?.main}60;
    }
  `,
  resizeHandleH: css`
    flex: 0 0 4px;
    cursor: row-resize;
    background-color: transparent;
    transition: background-color 0.15s ease;
    &:hover {
      background-color: ${theme?.palette?.primary?.main}40;
    }
    &:active {
      background-color: ${theme?.palette?.primary?.main}60;
    }
  `,
  warningBanner: css`
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 6px;
    background-color: ${theme?.palette?.background?.paper};
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid ${theme?.palette?.warning?.main}40;
    color: ${theme?.palette?.warning?.main};
  `,
});

export default DriverMap;
